import { broker, locateProxy } from '#~/agent/index.js';
import { fetchCalibration } from '#~/api/device/registry/index.js';
import { complement, retry } from '#~/control/calibration/commands/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createCalibrationCompletionController } from '#~/analytics/domains/calibration-lifecycle.js';
import { useCalibrate } from '#~/control/components/hooks.js';
import { depot } from '#~/depot/index.js';
import { isDeviceDeleted } from '#~/device/commands/teardown.js';
import { isControlDevice } from '#~/device/index.js';
import { getTemplate } from '#~/device/registry/commands/index.js';
import { logger } from '#~/logger/index.js';
import { post } from '#~/notifications/index.js';
import { useRoom } from '#~/room/hooks.js';
import { MessageIds } from './constants.js';
import type { Notification } from './types.js';
import { usePrompt } from '@studio/design-system/prompt';
import { Status } from '@studio/models/control/calibration';
import { isTransient } from '@studio/models/device';
import type { Device } from '@studio/models/device';
import { useLatestFn, useLazyRef, useMount } from '@studio/react-utils/hooks';
import { isNotEmpty, toObjectEntries } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

/** @internal */
const toMessageId = (id: Device['id']): string => [MessageIds.calibration, id].join('.');

/** `<Calibration>` component. */
export const Calibration = (): null => {
  const calibration = useLazyRef(() => new Map<Device['id'], Status>()).current;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const calibrate = useCalibrate();

  const { getAccessTokenSilently } = useAuth0();

  const { track } = useAnalytics();

  // Calibration completion controller (ENG-4026). Owns the TD1/TD2/TD3
  // bookkeeping (cancel gate, RUNNING-start timing, attempt-id consume) as a
  // single tested primitive. REMOVE the cancel-gate behavior when the bridge
  // proto CALIBRATION_STATE_CANCELLED lands — see ENG-4026 RFD. Constructed
  // once; the `track` fn comes from useAnalytics() like hooks.tsx does.
  const completion = useLazyRef(() => createCalibrationCompletionController(track)).current;

  const prompt = usePrompt();

  const [agentIds, roomId] = useRoom((room) => {
    const { devices } = room;

    // Compute differences in calibration data.
    const diffs: Notification[] = [];

    for (const [id, device] of toObjectEntries(devices)) {
      // Get this control device's calibration status (and error, read under
      // the same isControlDevice guard since `device.control` is optional).
      const calibrationState = isControlDevice(device) ? device.control.calibration : undefined;
      const status = calibrationState?.status;

      if (!status) {
        calibration.delete(id);

        continue;
      }

      const previousStatus = calibration.get(id);

      // Record the moment a device FIRST enters RUNNING (attempt start) so we
      // can report attempt_duration_ms at the terminal transition. Guard on
      // `previousStatus !== RUNNING`: this selector re-runs on every depot
      // change (zustand + shallow), so an unconditional call would overwrite
      // the start time on each tick and corrupt attempt_duration_ms (ENG-4026,
      // Devin review). Only fire on a genuine entry into RUNNING — which also
      // resets the start correctly on a cancel/retry (e.g. NULL->RUNNING).
      if (
        status === Status.CALIBRATION_STATE_RUNNING &&
        previousStatus !== Status.CALIBRATION_STATE_RUNNING
      ) {
        completion.noteRunningStart(id);
      }

      // If the previous status was defined and is now different, then we have a diff.
      if (calibration.has(id) && previousStatus !== status) {
        diffs.push({ id, name: device.name, status });

        // Feed the transition to the completion controller. It applies TD2
        // (first-transition-out-of-RUNNING) and TD3 (cancel gate) internally,
        // returning `none` for non-terminal transitions — so the existing
        // notification/toast behavior below is untouched (acceptance #8).
        completion.handleTransition(id, previousStatus, status, calibrationState?.error);
      }

      // Keep track of the previous calibration status.
      calibration.set(id, status);
    }

    // Remove any devices that are no longer present.
    for (const id of calibration.keys()) {
      if (!devices[id]) {
        calibration.delete(id);
      }
    }

    /** Did we find differences? */
    if (isNotEmpty(diffs)) {
      setNotifications(diffs);
    }

    return [room.agentIds, room.id];
  }, shallow);

  useEffect(() => {
    if (notifications.length === 0) {
      return;
    }

    // Post calibration notifications.
    notifications.forEach(({ id: deviceId, name, status }) => {
      const messageId = toMessageId(deviceId);

      if (status === Status.CALIBRATION_STATE_FAIL) {
        post({
          actionable: true,
          id: messageId,
          message: `An error occurred during calibration. Please make sure the camera ${name} is not pointed towards a wall and there is enough light in the room.`,
          severity: 'error',
          title: 'Calibration Failed'
        });

        prompt.show({
          title: 'Retry Calibration?',
          cancel: {
            label: 'Use Generic Driver'
          },
          onCancelClick: () => {
            // TD3 (ENG-4026): "Use Generic Driver" is a user-initiated cancel
            // of the precise calibration — arm the gate before complement().
            completion.recordCancel(deviceId);
            complement(deviceId);
          },
          okay: {
            label: 'Retry Calibration',
            onClick: () => {
              retry(deviceId);
            }
          },
          footNotes: (
            <button
              onClick={() => {
                // TD3 (ENG-4026): "Skip Calibration" abandons the attempt —
                // arm the gate so a stray RUNNING->OK is not mis-reported.
                completion.recordCancel(deviceId);
                prompt.hide();
              }}
              className='mt-4 px-8 py-4 text-muted underline'
            >
              Skip Calibration
            </button>
          ),
          message:
            'Calibration has partially failed. You may retry the failed step again to complete calibration, or opt for a generic driver with less precision. You can also recalibrate your camera later.'
        });
      } else if (status === Status.CALIBRATION_STATE_OK_GENERIC) {
        post({
          id: messageId,
          message: `Your source ${name} is now using a generic driver. You can retry calibration in advanced settings to complete full camera calibration.`,
          severity: 'info',
          title: 'Generic driver enabled.'
        });
      } else if (
        status === Status.CALIBRATION_STATE_OK ||
        status === Status.CALIBRATION_STATE_SUCCESS
      ) {
        post({
          id: messageId,
          message: `The camera ${name} is now calibrated! You can now use all the magic movement tools.`,
          severity: 'success',
          title: 'Calibration complete.'
        });
      } else if (status === Status.CALIBRATION_STATE_RUNNING) {
        post({
          id: messageId,
          message: `The camera ${name} is being calibrated. This process will take a few minutes...`,
          severity: 'info',
          title: 'Calibrating...'
        });
      }
    });

    // Reset notifications.
    setNotifications([]);
  }, [notifications]);

  const handleDeviceAdded = useLatestFn(async (deviceId: Device['id']) => {
    if (isTransient(deviceId)) {
      return;
    }

    // ENG-4477: this flow re-enters via a 500ms capabilities poll and spans
    // several awaited fetches — a source deleted mid-load must stop it here
    // rather than let a stale iteration keep running against the tombstone.
    if (isDeviceDeleted(deviceId)) {
      return;
    }

    const device = depot.getState().rooms[roomId]?.devices[deviceId];

    if (!device) {
      logger.warning(`[PG-917]: Unable to locate device: ${deviceId}`);
      return;
    }

    if (!isControlDevice(device)) {
      return;
    }

    const proxy = locateProxy(deviceId);
    const capabilities = proxy?.state.status?.device[deviceId]?.capabilities;

    if (!capabilities) {
      setTimeout(() => handleDeviceAdded(deviceId), 500);
      return;
    }

    const movementCapabilities = capabilities.movement?.vectors;

    if (
      !movementCapabilities ||
      !movementCapabilities.pan?.absoluteMove ||
      !movementCapabilities.tilt?.absoluteMove
    ) {
      return;
    }

    const token = await getAccessTokenSilently();

    // Look up the device configuration.
    const template = await getTemplate(device.modelId, token);

    // ENG-4495 C7: gate this watchdog AUTO-calibration on the authoritative generic bit — the same
    // honest signal as the context menu (C6), replacing the fragile name.includes('other') heuristic
    // (which missed generic models whose name lacked "other" and false-matched names like "Brother").
    // A driverless model can never calibrate, so never auto-trigger the doomed calibration on it; the
    // template is already resolved here (awaited), and an absent model also skips — matching today's
    // "unknown → don't auto-calibrate" safety.
    if (!template?.model || template.model.generic === true) {
      return;
    }

    /*
     * ENG-4477: the token/template fetches above can take seconds — the exact
     * window in which the repro deletes the source ("delete while settings
     * are still loading"). Re-check the tombstone AND the depot after those
     * awaits so a delete mid-load cancels the flow instead of letting an
     * orphaned calibration mutation fire at the torn-down device and race
     * state sync.
     */
    if (isDeviceDeleted(deviceId) || !depot.getState().rooms[roomId]?.devices[deviceId]) {
      return;
    }

    // Look up the device calibration.
    await fetchCalibration(device.modelId, device.id, token).catch((_e) => {
      // No calibration profile is available.
      queueMicrotask(() => {
        // ENG-4477: same guard after the calibration fetch — the last await
        // before the mutation goes out.
        if (isDeviceDeleted(deviceId)) {
          return;
        }

        calibrate(deviceId);
      });
    });
  });

  // Listen for devices being added to this room.
  useMount(() => {
    const agents = agentIds.map((id) => broker.acquire(id)).filter(isNotEmpty);

    if (agents.length === 0) {
      return noop;
    }

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const fn = (deviceId: Device['id']): void => {
      handleDeviceAdded(deviceId).catch((error) => {
        logger.error(error);
      });
    };

    agents.forEach((agent) => agent.on('device.added', fn));

    return () => {
      agents.forEach((agent) => {
        agent.off('device.added', fn);
      });
    };
  });

  return null;
};
